import React, {
	useImperativeHandle,
	forwardRef,
	useState,
	useEffect,
} from 'react'
import {EditorContent} from '@tiptap/react'
import useTiptapEditor from 'shared/hooks/useTiptapEditor'
import Style from './style'

const TiptapEditor = forwardRef(
	(
		{
			initialContent = '',
			placeholder = 'Enter your content...',
			editable = true,
			onUpdate,
			className,
			...props
		},
		ref
	) => {
		const [, forceUpdate] = useState({})
		const [isFocused, setIsFocused] = useState(false)

		const {
			editor,
			getContent,
			getText,
			setContent,
			clearContent,
			focus,
			blur,
			isEmpty,
		} = useTiptapEditor(initialContent, {
			placeholder,
			editable,
			onUpdate: (params) => {
				// Force re-render when content changes
				forceUpdate({})
				if (onUpdate) {
					onUpdate(params)
				}
			},
		})

		// Force re-render when selection changes
		useEffect(() => {
			if (!editor) return

			const handleSelectionUpdate = () => {
				forceUpdate({})
			}

			const handleFocus = () => {
				setIsFocused(true)
			}

			const handleBlur = () => {
				setIsFocused(false)
			}

			editor.on('selectionUpdate', handleSelectionUpdate)
			editor.on('transaction', handleSelectionUpdate)
			editor.on('focus', handleFocus)
			editor.on('blur', handleBlur)

			return () => {
				editor.off('selectionUpdate', handleSelectionUpdate)
				editor.off('transaction', handleSelectionUpdate)
				editor.off('focus', handleFocus)
				editor.off('blur', handleBlur)
			}
		}, [editor])

		// Expose methods to parent component via ref
		useImperativeHandle(ref, () => ({
			getContent,
			getText,
			setContent,
			clearContent,
			focus,
			blur,
			isEmpty,
			editor,
		}))

		if (!editor) {
			return <Style.LoadingContainer>Loading editor...</Style.LoadingContainer>
		}

		return (
			<Style.EditorContainer
				className={className}
				$editable={editable}
				$focused={isFocused}
				onClick={(e) => {
					if (editable && editor) {
						const target = e.target

						// Don't focus if clicking on toolbar elements
						if (target.closest('[data-toolbar]')) {
							return
						}

						// If clicking in empty area, focus at the end
						if (
							target.classList.contains('ProseMirror') ||
							target.closest('[data-editor-content]')
						) {
							editor.commands.focus()
						} else {
							// Focus at the end of content when clicking in empty areas
							editor.commands.focus('end')
						}
					}
				}}
				{...props}
			>
				{editable && (
					<Style.Toolbar data-toolbar>
						<Style.FontSizeSelect
							onChange={(e) => {
								if (e.target.value) {
									// Clear any heading first, then apply font size
									editor
										.chain()
										.focus()
										.clearNodes()
										.setFontSize(e.target.value)
										.run()
								} else {
									editor.chain().focus().unsetFontSize().run()
								}
							}}
							value={
								// Get font-size from selected text or current position
								editor.getAttributes('textStyle')?.fontSize ||
								editor.getAttributes('fontSize')?.fontSize ||
								''
							}
						>
							<option value='12px'>12px</option>
							<option value='14px'>14px</option>
							<option value='16px'>16px</option>
							<option value='18px'>18px</option>
							<option value='20px'>20px</option>
							<option value='24px'>24px</option>
							<option value='28px'>28px</option>
							<option value='32px'>32px</option>
						</Style.FontSizeSelect>

						<Style.ToolbarButton
							onClick={() => editor.chain().focus().toggleBold().run()}
							$active={editor.isActive('bold')}
							type='button'
						>
							<strong>B</strong>
						</Style.ToolbarButton>
						<Style.ToolbarButton
							onClick={() => editor.chain().focus().toggleItalic().run()}
							$active={editor.isActive('italic')}
							type='button'
						>
							<em>I</em>
						</Style.ToolbarButton>
						<Style.ToolbarButton
							onClick={() => editor.chain().focus().toggleStrike().run()}
							$active={editor.isActive('strike')}
							type='button'
						>
							<s>S</s>
						</Style.ToolbarButton>

						<Style.ToolbarButton
							onClick={() => {
								if (editor.isActive('heading', {level: 1})) {
									editor.chain().focus().toggleHeading({level: 1}).run()
								} else {
									editor
										.chain()
										.focus()
										.unsetFontSize()
										.toggleHeading({level: 1})
										.run()
								}
							}}
							$active={editor.isActive('heading', {level: 1})}
							type='button'
						>
							H1
						</Style.ToolbarButton>
						<Style.ToolbarButton
							onClick={() => {
								if (editor.isActive('heading', {level: 2})) {
									editor.chain().focus().toggleHeading({level: 2}).run()
								} else {
									editor
										.chain()
										.focus()
										.unsetFontSize()
										.toggleHeading({level: 2})
										.run()
								}
							}}
							$active={editor.isActive('heading', {level: 2})}
							type='button'
						>
							H2
						</Style.ToolbarButton>
						<Style.ToolbarButton
							onClick={() => {
								if (editor.isActive('heading', {level: 3})) {
									editor.chain().focus().toggleHeading({level: 3}).run()
								} else {
									editor
										.chain()
										.focus()
										.unsetFontSize()
										.toggleHeading({level: 3})
										.run()
								}
							}}
							$active={editor.isActive('heading', {level: 3})}
							type='button'
						>
							H3
						</Style.ToolbarButton>

						<Style.ToolbarButton
							onClick={() => editor.chain().focus().toggleBulletList().run()}
							$active={editor.isActive('bulletList')}
							type='button'
						>
							• List
						</Style.ToolbarButton>
						<Style.ToolbarButton
							onClick={() => editor.chain().focus().toggleOrderedList().run()}
							$active={editor.isActive('orderedList')}
							type='button'
						>
							1. List
						</Style.ToolbarButton>

						{/* <Style.ToolbarButton
							onClick={() => {
								editor.chain().focus().unsetFontSize().setParagraph().run()
							}}
							$active={editor.isActive('paragraph')}
							type='button'
						>
							P
						</Style.ToolbarButton> */}
						<Style.ToolbarButton
							onClick={() => editor.chain().focus().toggleBlockquote().run()}
							$active={editor.isActive('blockquote')}
							type='button'
						>
							"
						</Style.ToolbarButton>
					</Style.Toolbar>
				)}

				<Style.EditorContent data-editor-content>
					<EditorContent editor={editor} />
				</Style.EditorContent>
			</Style.EditorContainer>
		)
	}
)

TiptapEditor.displayName = 'TiptapEditor'

export default TiptapEditor
